import { lazy, Suspense, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { useBamAuth, useNavItems } from '@/auth';
import { usePageTitle } from '@/hooks/usePageMeta';
import { envVariables } from '@/utils/env';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Navbar from './components/navBar/Navbar';
import { LoadingProvider } from './contextProviders/loadingProvider';
import { ThemeContextProvider } from './contextProviders/themeProvider';
import { transientStatusToastContainerId } from './queries/toastContainers';
import { queryConfig } from './queries/utils';
import InternetConnectionStatus from './views/parts/InternetConnectionStatus';
import RootView from './views/rootView/RootView';

import 'react-toastify/dist/ReactToastify.css';

const PermissionSwitcher = envVariables().featurePermissionSwitcher
  ? lazy(() => import('@/auth/dev/PermissionSwitcher'))
  : null;

const queryClient = new QueryClient(queryConfig());

/** App's router <Outlet /> is in RootView.
 *
 * @returns ReactNode
 */
const App = () => {
  const { hasPermission } = useBamAuth();
  const menuItems = useNavItems();
  const location = useLocation();
  const path = location.pathname;

  usePageTitle();
  /**
   * Check the current routes to show the generic navbar (or not).
   * Leave the navbar as is for searching in the Mwd in BamClientView.
   * Placed here to not mess with the SSE unit tests, which use RootView with an argument,
   * therefor it cannot work with useMatches via <RouterProvider router={router}
   */
  const { featureAgenda, featureLmm, featureWts } = envVariables();
  const showGenericNavbar = useMemo<boolean>(
    () =>
      (['/', '/mwd-v2', '/status'].includes(path) && hasPermission('platform.view')) ||
      (['/plank', '/plank/regionaal'].includes(path) && hasPermission('ddp.view')) ||
      (path === '/b-regelingen' && hasPermission('bregelingen.view')) ||
      (path === '/agenda' && hasPermission('agenda.view', !!featureAgenda)) ||
      (path === '/beheer' &&
        (hasPermission('beheer.tam') || hasPermission('beheer.matbeheer') || hasPermission('lmm.view'))) ||
      (path === '/lmm' && hasPermission('lmm.view', !!featureLmm)) ||
      (path === '/beheer/uitsluitingen' && hasPermission('lmm.view', !!featureWts)),
    [path, hasPermission, featureAgenda, featureLmm, featureWts],
  );
  return (
    <>
      <ThemeContextProvider>
        <QueryClientProvider client={queryClient}>
          <LoadingProvider>
            {showGenericNavbar && <Navbar menuItems={menuItems} />}
            <RootView />
          </LoadingProvider>
        </QueryClientProvider>
      </ThemeContextProvider>
      <ToastContainer limit={1} />
      <ToastContainer containerId={transientStatusToastContainerId} limit={1} />
      <InternetConnectionStatus />
      {PermissionSwitcher && (
        <Suspense>
          <PermissionSwitcher />
        </Suspense>
      )}
    </>
  );
};

export default App;
